import { AnyAction } from 'redux';
import { RefreshTokenStatus, SingupType, IAccount } from 'src/types/login';
import { PassengersErrorMessagesEnum } from 'src/types/global';
import { IAuthorization } from 'src/api/types';

import { LoginActionType } from './actionType';
import { PrivacyPolicy, ReduxDispatch } from '../type';
import {
    getGoogleApiInterfaceStatus,
    initialGoogleApiCounter,
    isGoogleApiInterfaceCounterExceeded,
} from 'src/utilis/utilis';
import {
    setGoogleApiInterfaceIntervalAction,
    setIsGoogleApiCounterExceededPopUpOpenAction,
    setIsGoogleApiInterfaceCounterExceededAction,
    setIsRedirectPopupOpenAction,
} from '../slices/common/commonSlice';

/* eslint-disable import/no-cycle */
import store from '..';
import { setClientCodeAction } from '../slices/optimizations/optimizationsSlice';

export const initConnect = <T = {}>(payload: T): ReduxDispatch<T> => {
    return {
        type: LoginActionType.initialConfiguration,
        payload,
    };
};

export const initConnectSuccess = <T = {}>(payload: T): ReduxDispatch<T> => {
    return {
        type: LoginActionType.initialConfigurationSuccess,
        payload,
    };
};

export const initConnectFail = <T = {}>(payload: T): ReduxDispatch<T> => {
    return {
        type: LoginActionType.initialConfigurationFail,
        payload,
    };
};

export const setAuthorizationToken = (payload: IAuthorization): ReduxDispatch<IAuthorization> => {
    return {
        type: LoginActionType.setAuthorizationToken,
        payload,
    };
};

export const setFcAccount = (payload: IAccount): ReduxDispatch<IAccount> => {
    if (payload && payload.accountCode) {
        store.store.dispatch(
            setClientCodeAction({
                clientCode: '',
            })
        );

        initialGoogleApiCounter(payload.accountCode).catch(() => {});

        getGoogleApiInterfaceStatus(payload.accountCode).then((data) => {
            if (data) {
                const isExceeded = isGoogleApiInterfaceCounterExceeded(data);
                store.store.dispatch(
                    setGoogleApiInterfaceIntervalAction({ interval: data.ClientsRequestsInterval })
                );

                if (isExceeded !== null) {
                    store.store.dispatch(setIsGoogleApiInterfaceCounterExceededAction({ isExceeded }));
                    store.store.dispatch(
                        setIsGoogleApiCounterExceededPopUpOpenAction({ isOpen: isExceeded })
                    );
                }
            }
        }).catch(() => {});
    }

    return {
        type: LoginActionType.SetFcAccount,
        payload,
    };
};

export const removeFcAccounts = (payload: IAccount[]): ReduxDispatch<IAccount[]> => {
    return {
        type: LoginActionType.RemoveFcAccounts,
        payload,
    };
};

export const setDate = <Date>(payload: Date): ReduxDispatch<Date> => {
    return {
        type: LoginActionType.SetDate,
        payload,
    };
};

export const setLogout = (): AnyAction => {
    return {
        type: LoginActionType.Logout,
    };
};

export const setLoginType = (payload: SingupType): AnyAction => {
    return {
        type: LoginActionType.SetLoginType,
        payload,
    };
};

export const setTokenRefreshStatus = (payload: RefreshTokenStatus): AnyAction => {
    return {
        type: LoginActionType.SetTokenRefreshStatus,
        payload,
    };
};

export const setErrorMessage = (payload: string | undefined): AnyAction => {
    return {
        type: LoginActionType.SetErrorMessage,
        payload,
    };
};

export const setErrorCode = (payload: PassengersErrorMessagesEnum | undefined): AnyAction => {
    return {
        type: LoginActionType.SetErrorCode,
        payload,
    };
};

export const setNeedsPrivacyPolicy = (payload: boolean): AnyAction => {
    return {
        type: LoginActionType.setNeedsPrivacyPolicy,
        payload,
    }
};

export const setPrivacyPolicy = (payload: PrivacyPolicy): AnyAction => {
    return {
        type: LoginActionType.setPrivacyPolicy,
        payload,
    }
};

export const setPinCode = (payload: string): AnyAction => {
    return {
        type: LoginActionType.setPinCode,
        payload,
    }
};

export const setUserMobile = (payload: string): AnyAction => {
    return {
        type: LoginActionType.setUserMobile,
        payload,
    }
};

const getLoginState = () =>
    (store.store.getState() as unknown as {
        loginReducer: { selectedFcAccount: IAccount | null; fcAccounts: IAccount[] };
    }).loginReducer;

/**
 * Handles companies whose token update failed during work: drops them and shows `message`.
 * When the company being worked on is one of them, switches to the first company left through
 * setFcAccount, so its side effects run. With no company left, asks the user to log in again.
 */
export const dropUnavailableFcAccounts = (unavailable: IAccount[], message: string): void => {
    if (!unavailable.length) return;

    const dropped = new Set(unavailable.map((account) => account.contactUUID));
    const { fcAccounts = [], selectedFcAccount: before } = getLoginState();
    if (!fcAccounts.some((account) => !dropped.has(account.contactUUID))) {
        store.store.dispatch(setIsRedirectPopupOpenAction({ isRedirectPopupOpen: true, reason: 'refresh_failed' }));
        return;
    }

    store.store.dispatch(removeFcAccounts(unavailable));
    const after = getLoginState().selectedFcAccount;
    if (after && after.contactUUID !== before?.contactUUID) {
        store.store.dispatch(setFcAccount(after));
    }
    store.store.dispatch(setErrorMessage(message));
};